<template>
    <div>
        <BillingPlanSummary
            :isFTC="isFTC"
            :internationalizedPriceWithUnit="internationalizedPriceWithUnit"
            :countryClass="countryClass"
            :pageAttr="pageAttr"
            :plan="plan"
            :isFromDirect="isFromDirect"
            :coupon="coupon"></BillingPlanSummary>
        <BillingPromoSummary
            v-if="showPromoComponent"
            ref="promoSummary"
            :isFTC="isFTC"
            :formConfig="formConfig"
            :displayTrialInfo="displayTrialInfo"
            :orderSummaryConfig="orderSummaryConfig"
            :form="form"
            :coupon="coupon"
            :keydownHandler="handleEnterKey"
            :buttonConfig="buttonConfig"
            :plan="plan"
            :handleClickCouponCTA="handleClickCouponCTA"
            @reapplyCoupon="attemptApplyCoupon"
            :countryClass="countryClass"
            :internationalizedPrice="internationalizedPrice"
            :pricing="pricing"
            :showSubTotalNow="showSubTotalNow"
            :pageAttr="pageAttr"
            :trialAffectingPricing="trialAffectingPricing"
            :nextNonZeroBillingPrice="nextNonZeroBillingPrice"></BillingPromoSummary>
        <BillingPaymentSummary
            :isFTC="isFTC"
            :countryClass="countryClass"
            :disclaimerCopyEl="disclaimerCopyEl"
            :nextBillingDate="nextBillingDate"
            :pageAttr="pageAttr"
            :plan="plan"
            :nextNonZeroBillingPrice="nextNonZeroBillingPrice"
            :showGiftBalance="showGiftBalance"></BillingPaymentSummary>
        <input
            v-if="plan && plan.recurlyCode"
            type="hidden"
            data-recurly="plan"
            :value="plan.recurlyCode" />
        <input ref="couponRecurly" value="" type="hidden" data-recurly="coupon" />
    </div>
</template>

<script>
    import { mapGetters, mapMutations } from 'vuex';
    import InputElm from 'atoms/InputElm';
    import InputField from 'atoms/InputField';
    import pricePrettyPrint from 'aa/vue/plugins/priceMixin';
    import ButtonCta from 'atoms/ButtonCta';
    import { OrderSummaryConfig } from 'aa/vue/FormConfig';
    import WaitingOverlay from 'aa/vue/atoms/WaitingOverlay';
    import { DateFormatter, getEndDate } from 'aa/helpers/DateFormatter';
    import Domain from 'helpers/Domain';
    import BillingPlanSummary from 'aa/vue/components/BillingPlanSummary';
    import BillingPromoSummary from 'aa/vue/components/BillingPromoSummary';
    import BillingPaymentSummary from 'aa/vue/components/BillingPaymentSummary';
    import { EXP_AB_TEST_INTL_NO_FREE_TRIAL } from 'aa/helpers/featureConstants';
    import currentSubscriptionMixin from './account/currentSubscriptionMixin';
    import { COUPON_TYPES } from 'aa/vue/models/CouponDiscount';

    export default {
        components: {
            InputElm,
            InputField,
            ButtonCta,
            WaitingOverlay,
            BillingPlanSummary,
            BillingPromoSummary,
            BillingPaymentSummary,
        },
        props: {
            coupon: {
                type: Object,
            },
            couponDisclaimer: {
                type: String,
            },
            couponCheckPending: {
                type: Boolean,
            },
            recurly: {
                type: Object,
            },
            recurlyReady: {
                type: Boolean,
                default: false,
            },
            annualPlanOption: {
                type: Boolean,
                default: true,
            },
            formConfig: {
                type: Object,
                default: null,
            },
            recurlyAccountBalance: {
                type: Number,
                default: 0,
            },
            nextNonZeroBillingPrice: {
                type: String,
            },
            pricing: {
                type: Object,
            },
            pageAttr: {
                type: Object,
            },
            couponDiscountBillingDate: {
                type: String,
                default: null,
            },
            isFTC: {
                type: Boolean,
                default: false,
            },
            isFromDirect: {
                type: Boolean,
                default: false,
            },
            showCheckbox: {
                type: Boolean,
                default: false,
            },
        },

        mixins: [pricePrettyPrint, currentSubscriptionMixin],
        data: function () {
            return {
                isPaymentPage: true,
                form: {},
                countryClass: '',
                internationalizedPrice: null,
                internationalizedZeroPrice: null,
                internationalizedPriceWithUnit: null,
                orderSummaryConfigDefault: OrderSummaryConfig,
            };
        },

        computed: {
            orderSummaryConfig() {
                return this.formConfig || this.orderSummaryConfigDefault;
            },

            ...mapGetters('user', ['isNoTrialUser', 'isSubscriber', 'isExSubscriber']),

            ...mapGetters(['bundleShowtime']),
            ...mapGetters('plan', ['getSelectedPlan', 'isSwitchPlan']),

            ...mapGetters(['featureIsActive']),
            ...mapGetters('variant', ['isNoTrialVariant']),
            ...mapGetters('flow', ['isRendezvous']),

            displayTrialInfo() {
                if (this.isABTestNoTrialFeature && this.isNoTrialVariant && !this.isRendezvous) {
                    return '';
                }

                if (!this.isTrialAllowed) {
                    return false;
                }
                if (this.isFTC) {
                    return false;
                }
                return this.trialAffectingPricing;
            },

            trialAffectingPricing() {
                return !!this.coupon?.trialString || !!this.plan?.trialString;
            },

            showGiftBalance() {
                return this.isFTC && this.recurlyAccountBalance > 0;
            },

            isTrialAllowed() {
                // US ex-subs without trial extension or coupon not eligible for free trial
                if (Domain.isDomestic()) {
                    if (this.isSwitchPlan && this.isFTC) {
                        return !!this.coupon?.trialString;
                    }
                    return this.isNoTrialUser ? !!this.coupon?.trialString : true;
                }

                return true;
            },

            isInternational() {
                return Domain.isInternational();
            },

            isNoTrialUserWithCouponTrial() {
                return this.isNoTrialUser && !!this.coupon.trialString;
            },

            isNoTrialUserWithoutCouponTrial() {
                return this.isNoTrialUser && !this.coupon.trialString;
            },

            hasPromoCode() {
                return this.$store.state.promo == null ? false : true;
            },

            promoSuppressedByShowtimeBundleCopy() {
                if (this.pageAttr.aa_promo_suppressed_message_showtime === undefined) {
                    return '';
                } else {
                    return this.pageAttr.aa_promo_suppressed_message_showtime.replace(
                        '%COUPON_CODE%',
                        this.$store.state.promo,
                    );
                }
            },

            promotionalCode() {
                if (this.hasPromoCode) {
                    return this.$store.state.promo;
                }
                return '';
            },

            plan() {
                const selectedPlan = this.$store.getters['plan/getSelectedPlan'];

                if (selectedPlan?.trialString && this.isNoTrialUser) {
                    selectedPlan.trialString = null;
                }

                return selectedPlan;
            },

            couponTrialEndDate() {
                let discount = !this.coupon.recurlyInfo ? null : this.coupon.recurlyInfo.discount;

                if (!discount || discount.type !== 'free_trial') {
                    return null;
                }

                try {
                    let { amount, unit } = discount.trial;
                    let trialEndDate = getEndDate(amount, unit);
                    return DateFormatter.format(trialEndDate, null, CBS.Registry.region.dateLocale);
                } catch (e) {
                    return null;
                }
            },

            getTodaysDateFormatted() {
                return DateFormatter.format(new Date(), null, CBS.Registry.region.dateLocale);
            },

            nextBillingDate() {
                // Next Billing Date Priority:
                // 1. Switch Plan -> downgrade vs upgrade
                // 2. Coupon Trial End Date
                // 3. Plan Trial End Date
                // 4. Today's Date (No Coupon or Plan Trial)
                let nextBillingDate;
                if (this.isSwitchPlan) {
                    const switchDirection = this.getSelectedPlan?.switchDirection;

                    if (switchDirection === 'downgrade') {
                        nextBillingDate = DateFormatter.format(
                            new Date(this.currentSubscription?.next_bill_date.date),
                            null,
                            CBS.Registry.region.dateLocale,
                        );
                    } else if (switchDirection === 'upgrade') {
                        nextBillingDate = this.getTodaysDateFormatted;
                    }

                    this.$emit('trialEndDateFormatted', nextBillingDate);

                    return nextBillingDate;
                }

                // based on 100% coupon off
                if (this.couponDiscountBillingDate) {
                    return this.couponDiscountBillingDate;
                }

                const planTrialDate = this.isTrialAllowed
                    ? this.plan?.next_billing_date || this.plan?.trialEndDate
                    : undefined;

                if (this.couponTrialEndDate) {
                    nextBillingDate = this.couponTrialEndDate;
                } else if (planTrialDate) {
                    nextBillingDate = planTrialDate;
                } else {
                    nextBillingDate = this.getTodaysDateFormatted;
                }
                this.$emit('trialEndDateFormatted', nextBillingDate);

                return nextBillingDate;
            },

            disclaimerCopyEl() {
                let trialEndEl = `<span id="trial-end-date" data-original="${this.plan?.trialEndDate}">${this.plan?.trialEndDate}</span>`;
                let billingSummaryCopy = this.isSubscriber
                    ? this.pageAttr.billing_summary_trial_end_copy_subs
                    : this.pageAttr.billing_summary_trial_end_copy;

                return (billingSummaryCopy || '').replace('%d', trialEndEl).trim();
            },

            buttonConfig() {
                return Object.assign(
                    {
                        displayText:
                            this?.pageAttr?.billing_summary_coupon_field_cta ??
                            this.$getLocale('ok'),
                        event: this.handleClickCouponCTA,
                    },
                    OrderSummaryConfig.buttonConfig,
                );
            },

            showSubTotalNow() {
                return !this.isTrialAllowed || this.isInternational;
            },

            showSubTotalNext() {
                return !this.isTrialAllowed && !this.isNoTrialUserWithCouponTrial;
            },

            showIntlZeroPrice() {
                return !this.isTrialAllowed && this.isNoTrialUserWithCouponTrial;
            },
            showPromoComponent() {
                // only show coupon field for Sign Up (both regular and direct link)
                const allowedRoutes = ['PAYMENT_SUMMARY', 'DIRECTLINK'];

                if (allowedRoutes.includes(this.$route.name)) {
                    return true;
                }

                const hasValidCoupon = this.coupon?.recurlyInfo && !this.coupon.errorMessage;
                const hasFreeTrialExtension =
                    hasValidCoupon &&
                    this.coupon?.recurlyInfo?.discount?.type === COUPON_TYPES.FREE_TRIAL;

                // display for switch plan (including Direct Link SP if we have a valid coupon)
                // (Logic for hiding the Coupon Input field is inside BillingPromoSummary.vue)
                if (this.isSwitchPlan && hasValidCoupon && !hasFreeTrialExtension) {
                    return true;
                }

                return false;
            },
            isABTestNoTrialFeature() {
                return this.featureIsActive(EXP_AB_TEST_INTL_NO_FREE_TRIAL);
            },
        },

        methods: {
            ...mapMutations('ftc', ['setCoolingRightsWaived']),
            getInternationalizedPricesUsingRecurly() {
                if (this.recurly && this.getSelectedPlan) {
                    const selectedPlan = this.getSelectedPlan;
                    const { currencySymbol, currencyCode } = this.recurly.Pricing.Checkout();
                    const selectedPlanPrice = selectedPlan.rawPrice.replace(/,/g, '');

                    const priceLocaleOverride = this.$store.state.serverData?.priceLocaleOverride;
                    const overrideLocale =
                        priceLocaleOverride?.[CBS.Registry.region.property] ??
                        CBS.Registry.region.locale;

                    this.internationalizedZeroPrice = `${this.pricePrettyPrint(0, currencySymbol, currencyCode, overrideLocale)}`;
                    this.internationalizedPrice = `${this.pricePrettyPrint(
                        selectedPlanPrice,
                        currencySymbol,
                        currencyCode,
                        overrideLocale,
                    )}`;

                    this.internationalizedPriceWithUnit = `${this.pricePrettyPrint(
                        selectedPlanPrice,
                        currencySymbol,
                        currencyCode,
                        overrideLocale,
                    )} / ${selectedPlan.priceUnit}`;

                    this.$emit('found-price', {
                        internationalizedPrice: this.internationalizedPrice,
                        internationalizedPriceWithUnit: this.internationalizedPriceWithUnit,
                        internationalizedPriceUnformatted:
                            selectedPlan.priceInLowestUnit / selectedPlan.currency_subunits,
                    });
                }
            },

            handleClickCouponCTA(e) {
                e.preventDefault();

                if (this.showCheckbox) {
                    // Set the cooling rights waived state to false only if the checkbox flag is true to avoid breaking payments
                    this.setCoolingRightsWaived(false);
                }

                const couponCode = this.attachCurrentCouponToForm();
                if (this.form.couponCode?.value !== null && this.form.couponCode?.value !== '') {
                    this.$refs.couponRecurly.value = couponCode;
                } else {
                    //resetting coupon error message and css class
                    this.resetCouponFieldErrorMessage();
                }

                this.attemptApplyCoupon(couponCode);

                this.getInternationalizedPricesUsingRecurly();
            },

            handleEnterKey(e) {
                if (e.key === 'Enter' || e.code === 'Enter') {
                    e.stopPropagation();
                    e.preventDefault();

                    this.handleClickCouponCTA(e);
                }
                this.resetCouponFieldErrorMessage();
            },

            attemptApplyCoupon(couponCode) {
                if (this.plan?.recurlyCode) {
                    this.$emit('applyCoupon', {
                        plan: this.plan.recurlyCode,
                        couponCode: couponCode,
                    });
                }
            },
            attachCurrentCouponToForm(couponCodeOverride) {
                if (!this.form || !this.form.couponCode) {
                    return '';
                }
                // If couponCodeOverride is provided, use it; otherwise, use the value from the form
                const couponCode = couponCodeOverride
                    ? couponCodeOverride
                    : this.form.couponCode?.value || '';

                if (this.$refs.couponRecurly) {
                    this.$refs.couponRecurly.value = couponCode;
                }

                return couponCode;
            },

            resetCouponFieldErrorMessage() {
                document.querySelector('.field-wrapper--coupon-code').classList.remove('error');
                this.coupon.errorMessage = '';
                this.$emit('resetCoupon');
            },
            ftcUpdateSummaries() {
                const couponCode =
                    this.plan?.offerCouponCode ||
                    this.$refs?.couponRecurly.value ||
                    this.$store.state.promo ||
                    '';

                this.form.couponCode = {
                    value: couponCode,
                };
                if (this.orderSummaryConfig?.inputList?.couponCode) {
                    this.orderSummaryConfig.inputList.couponCode.value = couponCode;
                }
                this.attachCurrentCouponToForm(couponCode);

                this.$emit(
                    'updateOrder',
                    {
                        plan: this.plan.recurlyCode,
                        couponCode: couponCode,
                    },
                    false,
                );
                this.getInternationalizedPricesUsingRecurly();
            },
            updateSummaries() {
                const couponCode = this.attachCurrentCouponToForm();
                if (this.plan) {
                    this.$emit(
                        'updateOrder',
                        {
                            plan: this.plan.recurlyCode,
                            couponCode: couponCode,
                        },
                        false,
                    );
                }
                this.getInternationalizedPricesUsingRecurly();
            },
            processSummaries() {
                if (this.recurlyReady && this.isFTC) {
                    this.ftcUpdateSummaries();
                } else if (!this.isFTC) {
                    this.updateSummaries();
                }
            },
        },

        // Lifecycle Hooks
        beforeCreate: function () {
            Object.assign(OrderSummaryConfig.hiddenInputList.plan, {
                value: this.$store.getters['plan/getSelectedPlan']?.recurlyCode,
            });
        },

        created() {
            this.registrationCountry = this.$store.state.serverData.paymentForm?.country?.value
                ? this.$store.state.serverData.paymentForm?.country?.value.toLowerCase()
                : false;
            this.countryClass = `payment-summary-${this.registrationCountry}`;

            if (this.recurly) {
                if (this.isFTC) {
                    this.processSummaries();
                } else {
                    this.getInternationalizedPricesUsingRecurly();
                }
            }
        },

        watch: {
            recurly(newVal, oldVal) {
                if (oldVal === null && newVal !== null) {
                    this.getInternationalizedPricesUsingRecurly();
                }
            },
            recurlyReady(newVal) {
                if (newVal) {
                    this.processSummaries();
                }
            },

            plan(newVal) {
                this.processSummaries();
            },
            autoPromoChecked(newVal) {
                if (newVal === true) {
                    this.$nextTick(() => {
                        this.attachCurrentCouponToForm(
                            this.orderSummaryConfig?.inputList?.couponCode?.value,
                        );
                    });
                }
            },
        },
    };
</script>
